// The install panel. Copy is from docs/pwa/01-installable-web-app-design.md.
//
// Three rules it exists to keep:
//  - never a button that cannot install (see canOfferInstallButton);
//  - never a simulated native prompt for Safari — real instructions instead;
//  - never claim the app is absent elsewhere just because this context is a
//    browser. Standalone detection is about THIS window only.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Smartphone, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { canOfferInstallButton, manualInstructions, panelBody } from './installState';
import { useInstallPrompt } from './useInstallPrompt';

export const InstallPanel: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t } = useTranslation('common');
  const { mode, promptInstall } = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  const manual = manualInstructions(typeof navigator === 'undefined' ? '' : navigator.userAgent);
  const body = panelBody(mode);

  if (mode === 'installed' || mode === 'none') return null;
  // 'manual' with no known menu path is nothing honest to say.
  if (mode === 'manual' && !manual) return null;

  const install = async () => {
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="install-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">
            {t('install.title', 'KaziPeople, ready when you are')}
          </h3>
          {/* Per MODE. A single body line contradicted the block beneath it:
              "open from your Dock" above an Install button, or above
              how-to-install steps. See panelBody. */}
          {body && (
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
              {t(body.key, body.text)}
            </p>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('dismiss', 'Dismiss') as string}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      <div className="mt-3">
        {canOfferInstallButton(mode) && (
          <Button size="sm" onClick={install} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {t('install.cta', 'Install KaziPeople')}
          </Button>
        )}

        {/* No install event — Safari never has one, and Chromium only fires it
            under stricter criteria than its own menu requires. Real
            instructions for THIS browser, never a simulated prompt and never
            a blank panel. */}
        {mode === 'manual' && manual && (
          <div className="text-sm">
            <p className="font-medium text-foreground">{manual.label}</p>
            <p className="mt-1 text-muted-foreground">{manual.steps}</p>
          </div>
        )}

        {/* On a phone the honest answer is the real app, not a home-screen shim. */}
        {mode === 'mobile-store' && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Smartphone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {t('install.mobile', 'On your phone, get the KaziPeople mobile app from the App Store or Google Play.')}
          </p>
        )}
      </div>
    </div>
  );
};
